import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  Divider,
  Card,
  CardContent,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Source as SourceIcon,
  Person as PersonIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';

export const ViewDetails = ({ open, onClose, lead }) => {
  if (!lead) return null;

  // Helper functions
  const getLeadName = () => {
    return lead.fullName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unknown';
  };

  const getLeadEmail = () => {
    return lead.email || 'No email';
  };

  const getLeadPhone = () => {
    return lead.phoneNumber || lead.phone || 'No phone';
  };

  const getLeadCompany = () => {
    return lead.numberOfEmployees || lead.location || 'Orucore';
  };

  const getLeadSource = () => {
    return lead.source || lead.leadSource || 'Unknown';
  };

  const getLeadStatus = () => {
    return lead.leadType || 'New';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New':
        return { bgcolor: '#e0e7ff', color: '#4338ca' };
      case 'Qualified':
        return { bgcolor: '#f0fdf4', color: '#059669' };
      case 'Disqualified':
        return { bgcolor: '#fee2e2', color: '#dc2626' };
      case 'Closed - Won':
        return { bgcolor: '#ecfdf5', color: '#10b981' };
      case 'Closed - Loss':
        return { bgcolor: '#fef2f2', color: '#ef4444' };
      default:
        return { bgcolor: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getPlanTypeColor = (planType) => {
    switch (planType?.toLowerCase()) {
      case 'enterprise':
        return { bgcolor: '#fef3c7', color: '#d97706' };
      case 'professional':
        return { bgcolor: '#e0e7ff', color: '#4338ca' };
      case 'starter':
        return { bgcolor: '#f0f9ff', color: '#0284c7' };
      default:
        return { bgcolor: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCountryName = (countryCode) => {
    const countries = {
      'in': 'India',
      'us': 'United States',
      'uk': 'United Kingdom',
      'ca': 'Canada',
      'au': 'Australia',
      'de': 'Germany',
      'fr': 'France',
    };
    return countries[countryCode] || countryCode?.toUpperCase() || 'Unknown';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid #e2e8f0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#4f46e5', width: 50, height: 50 }}>
            {getInitials(getLeadName())}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              {getLeadName()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lead ID: {lead.leadId}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            bgcolor: '#f1f5f9',
            '&:hover': { bgcolor: '#e2e8f0' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Status and Plan Information */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Lead Status
                  </Typography>
                  <Chip
                    label={getLeadStatus()}
                    sx={{
                      ...getStatusColor(getLeadStatus()),
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {lead.planType && (
                    <Chip
                      label={`${lead.planType.charAt(0).toUpperCase() + lead.planType.slice(1)} Plan`}
                      sx={{
                        ...getPlanTypeColor(lead.planType),
                        fontWeight: 500
                      }}
                    />
                  )}
                  {lead.productName && (
                    <Chip
                      label={lead.productName}
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
              Contact Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmailIcon sx={{ color: '#64748b', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getLeadEmail()}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PhoneIcon sx={{ color: '#64748b', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getLeadPhone()}
                  </Typography>
                </Box>
              </Box>
              {lead.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocationIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {lead.location}
                    </Typography>
                  </Box>
                </Box>
              )}
              {lead.country && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FlagIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Country</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getCountryName(lead.country)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Lead Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
              Lead Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {lead.requestType && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Request Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {lead.requestType}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SourceIcon sx={{ color: '#64748b', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Source</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getLeadSource()}
                  </Typography>
                </Box>
              </Box>
              {lead.preferredContactMethod && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Preferred Contact</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {lead.preferredContactMethod.charAt(0).toUpperCase() + lead.preferredContactMethod.slice(1)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {lead.bestTimeToConnect && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ScheduleIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Best Time to Connect</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {lead.bestTimeToConnect.charAt(0).toUpperCase() + lead.bestTimeToConnect.slice(1)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Company Information */}
          {(lead.numberOfEmployees || getLeadCompany()) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
                Company Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Company/Details</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getLeadCompany()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Request Details */}
          {lead.reasonForRequest && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
                Request Details
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Typography variant="body1">
                  {lead.reasonForRequest}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Timeline Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
              Timeline
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarIcon sx={{ color: '#64748b', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Submitted At</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(lead.submittedAt)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarIcon sx={{ color: '#64748b', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Created At</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(lead.createdAt)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarIcon sx={{ color: '#64748b', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(lead.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderColor: '#e2e8f0', 
            color: '#64748b',
            '&:hover': { 
              borderColor: '#cbd5e1',
              bgcolor: '#f8fafc'
            }
          }}
        >
          Close
        </Button>
        {/* <Button
          variant="contained"
          color="success"
          sx={{
            bgcolor: '#059669',
            '&:hover': { bgcolor: '#047857' }
          }}
        >
          Convert to Customer
        </Button> */}
      </DialogActions>
    </Dialog>
  );
};

export default ViewDetails;